"use client";

import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Formik, Form, FieldArray } from "formik";
import * as Yup from "yup";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
  Link as MuiLink,
} from "@mui/material";
import {
  FaUserTie,
  FaGraduationCap,
  FaFileAlt,
  FaUpload,
  FaTrash,
  FaSave,
} from "react-icons/fa";
import api from "../../api/axiosInstance";
import { INSTITUTES } from "../../data/scholarship-applications";
import Swal from "sweetalert2";
import { extractApiErrorMessage } from "../../utils";

// Custom colors matching the original form
const colors = {
  primary: "#1e4d8c",
  secondary: "#3a7ca5",
  accent: "#f39c12",
  success: "#2ecc71",
  error: "#e74c3c",
  background: "#f9fafb",
  cardBg: "#ffffff",
  text: "#2c3e50",
  lightText: "#7f8c8d",
  border: "#e0e0e0",
  hover: "#edf2f7",
};

// Reapply form data interface
interface ReapplyFormData {
  name: string;
  cnic_number: string;
  gender: string;
  age: number;
  residential_address: string;
  cell_phone: string;
  telephone: string;
  email: string;

  // Educational History - only university with additional fields
  educational_history: {
    institute: string;
    score: number;
    score_is_percentage: boolean;
    is_university: boolean;
    current_semester?: number;
    total_semesters?: number;
  }[];

  // Scholarship Request Details - keep as is
  scholarship_request_details: {
    degree_discipline: string;
    degree_name: string;
    degree_institute: string;
    degree_institute_custom?: string;
    degree_recognized_by: string;
    degree_semester: number;
    tuition_fee_per_semester: number;
    admission_fee: number;
    tuition_fee_per_semester_in_words: string;
    self_contribution_percentage: number;
  };

  // Guardian Details - keep as is
  guardian_details: {
    name: string;
    cnic_number: string;
    cell_phone: string;
    occupation: string;
    designation: string;
    employer: string;
    monthly_income: number;
    pension: number;
    family_members_count: number;
  };

  // Documents - reduced list
  documents_attached: {
    title: string;
    attachment_url: string[];
  }[];
}

// Validation schema
const pkMobileRegex = /^(?:\+?92|0)?3[0-9]{2}[0-9]{7}$/;
const cnicRegex = /^(\d{5}-\d{7}-\d|\d{13})$/;

const validationSchema = Yup.object({
  name: Yup.string().required("Name is required"),
  cnic_number: Yup.string()
    .required("CNIC number is required")
    .matches(
      cnicRegex,
      "Enter CNIC as 12345-1234567-1 or 13 consecutive digits"
    ),
  gender: Yup.string().required("Gender is required"),
  age: Yup.number().required("Age is required").min(1, "Age must be positive"),
  residential_address: Yup.string().required("Residential address is required"),
  cell_phone: Yup.string()
    .required("Cell phone is required")
    .matches(pkMobileRegex, "Enter a valid Pakistani mobile number"),
  email: Yup.string()
    .email("Invalid email format")
    .required("Email is required"),

  educational_history: Yup.array().of(
    Yup.object({
      institute: Yup.string().required("Institute is required"),
      score: Yup.number()
        .required("Score is required")
        .min(0, "Score cannot be negative")
        .max(100, "Score cannot exceed 100"),
      current_semester: Yup.number()
        .required("Semester is required")
        .min(0, "Cannot be negative"),
      total_semesters: Yup.number()
        .required("Total is required")
        .min(1, "At least 1"),
    })
  ),

  scholarship_request_details: Yup.object({
    degree_discipline: Yup.string().required("Degree discipline is required"),
    degree_name: Yup.string().required("Degree name is required"),
    degree_institute: Yup.string().required("Degree institute is required"),
    degree_recognized_by: Yup.string().required(
      "Recognition authority is required"
    ),
    degree_semester: Yup.number()
      .required("Degree semester is required")
      .min(1, "Semester must be at least 1")
      .max(20, "Semester cannot exceed 20")
      .integer("Semester must be a whole number"),

    tuition_fee_per_semester: Yup.number()
      .required("Tuition fee is required")
      .min(1, "tuition_fee_per_semester cannot be negative"),
    tuition_fee_per_semester_in_words: Yup.string().required(
      "Tuition fee in words is required"
    ),
    admission_fee: Yup.number().required("Admission fee is required").min(0),
    self_contribution_percentage: Yup.number().min(0).max(100),
  }),

  guardian_details: Yup.object({
    name: Yup.string().required("Guardian name is required"),
    cnic_number: Yup.string()
      .required("Guardian CNIC number is required")
      .matches(
        cnicRegex,
        "Enter CNIC as 12345-1234567-1 or 13 consecutive digits"
      ),
    cell_phone: Yup.string().required("Guardian cell phone is required"),
    occupation: Yup.string().required("Guardian occupation is required"),
    designation: Yup.string().required("Guardian designation is required"),
    employer: Yup.string().required("Guardian employer is required"),
    monthly_income: Yup.number().required("Monthly income is required").min(0),
    family_members_count: Yup.number()
      .required("Family member count is required")
      .min(1),
  }),

  documents_attached: Yup.array().of(
    Yup.object({
      title: Yup.string().required("Document title is required"),
      attachment_url: Yup.array()
        .of(Yup.string())
        .min(1, "At least one file is required"),
    })
  ),
});

// Initial values
const initialValues: ReapplyFormData = {
  name: "",
  cnic_number: "",
  gender: "",
  age: 0,
  residential_address: "",
  cell_phone: "",
  telephone: "",
  email: "",

  educational_history: [
    {
      institute: "",
      score: 0,
      score_is_percentage: false,
      is_university: true,
      current_semester: 1,
      total_semesters: 8,
    },
  ],

  scholarship_request_details: {
    degree_discipline: "",
    degree_name: "",
    degree_institute: "",
    degree_recognized_by: "",
    degree_semester: 1,
    tuition_fee_per_semester: 0,
    admission_fee: 0,
    tuition_fee_per_semester_in_words: "",
    self_contribution_percentage: 0,
  },

  guardian_details: {
    name: "",
    cnic_number: "",
    cell_phone: "",
    occupation: "",
    designation: "",
    employer: "",
    monthly_income: 0,
    pension: 0,
    family_members_count: 0,
  },

  documents_attached: [
    { title: "Fee Voucher", attachment_url: [] },
    {
      title: "Last Paid Fee voucher (Proof of Fee payment)",
      attachment_url: [],
    },
    {
      title:
        "Latest Record of Results (CGPA) and marksheets of all past examinations",
      attachment_url: [],
    },
    { title: "Admission Letter", attachment_url: [] },
  ],
};

// Helper components
const SectionTitle = ({
  icon,
  title,
}: {
  icon: React.ReactNode;
  title: string;
}) => (
  <Box sx={{ display: "flex", alignItems: "center", mb: 3, mt: 4 }}>
    <Box sx={{ mr: 2, color: colors.primary, fontSize: "1.5rem" }}>{icon}</Box>
    <Typography variant="h5" sx={{ fontWeight: 600, color: colors.primary }}>
      {title}
    </Typography>
  </Box>
);

const uploadDocument = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append("file", file);
  const response = await api.post(
    "/scholarship-applications/documents/",
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
    }
  );
  return response.data.data.document_url;
};


export const ScholarshipReapplyForm = () => {
  const navigate = useNavigate();
  const { parent_application_id } = useParams();
  const [loading, setLoading] = useState(false);
  const [uploadingDocuments, setUploadingDocuments] = useState<{
    [key: number]: boolean;
  }>({});

  console.log("parent_application_id ==>", parent_application_id);
  const handleSubmit = async (values: ReapplyFormData) => {
    try {
      setLoading(true);

      // Add parent_application_id to the request body
      const requestData = {
        ...values,
        parent_application_id: parent_application_id
          ? parseInt(parent_application_id)
          : null,
      };

      await api.post("/scholarship-applications/", requestData);

      Swal.fire({
        title: "Success!",
        text: "Your reapplication has been submitted successfully.",
        icon: "success",
        confirmButtonText: "OK",
        confirmButtonColor: colors.success,
      }).then(() => {
        navigate("/applications");
      });
    } catch (error) {
      console.error("Error submitting reapplication:", error);
      Swal.fire({
        title: "Error!",
        text: extractApiErrorMessage(
          error,
          "Failed to submit reapplication. Please try again."
        ),
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: colors.error,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    docIndex: number,
    setFieldValue: (field: string, value: any) => void,
    values: ReapplyFormData
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingDocuments((prev) => ({ ...prev, [docIndex]: true }));

    try {
      const url = await uploadDocument(file);
      const currentUrls = values.documents_attached[docIndex].attachment_url;
      setFieldValue(`documents_attached.${docIndex}.attachment_url`, [
        ...currentUrls,
        url,
      ]);
    } catch (error) {
      console.error("Error uploading document:", error);
      Swal.fire({
        title: "Error!",
        text: extractApiErrorMessage(
          error,
          "Failed to upload document. Please try again."
        ),
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: colors.error,
      });
    } finally {
      setUploadingDocuments((prev) => ({ ...prev, [docIndex]: false }));
    }
  };

  return (
    <Container maxWidth={false} sx={{ py: 4, bgcolor: colors.background }}>
      <Paper
        elevation={3}
        sx={{
          p: 3,
          borderRadius: "12px",
          bgcolor: colors.cardBg,
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
            pb: 3,
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <FaGraduationCap size={40} color={colors.primary} />
            <Typography
              variant="h4"
              sx={{
                ml: 2,
                fontWeight: 700,
                color: colors.primary,
                letterSpacing: "0.5px",
              }}
            >
              Scholarship Reapplication Form
            </Typography>
          </Box>
        </Box>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            setFieldValue,
          }) => (
            <Form>
              <Box sx={{ maxWidth: "1200px", mx: "auto" }}>
                {/* Personal Information Section */}
                <SectionTitle
                  icon={<FaUserTie />}
                  title="Personal Information"
                />
                <Card
                  variant="outlined"
                  sx={{
                    mb: 4,
                    borderRadius: "10px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                    border: `1px solid ${colors.border}`,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: colors.primary,
                      py: 1,
                      px: 2,
                      borderTopLeftRadius: "10px",
                      borderTopRightRadius: "10px",
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ color: "white", fontWeight: 500 }}
                    >
                      Enter your personal details
                    </Typography>
                  </Box>
                  <CardContent sx={{ pt: 3 }}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          fullWidth
                          id="name"
                          name="name"
                          label="Full Name"
                          value={values.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.name && Boolean(errors.name)}
                          helperText={touched.name && errors.name}
                          variant="outlined"
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          fullWidth
                          id="cnic_number"
                          name="cnic_number"
                          label="CNIC Number"
                          value={values.cnic_number}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={
                            touched.cnic_number && Boolean(errors.cnic_number)
                          }
                          helperText={touched.cnic_number && errors.cnic_number}
                          variant="outlined"
                          placeholder="12345-1234567-1"
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} md={4}>
                        <FormControl
                          fullWidth
                          error={touched.gender && Boolean(errors.gender)}
                          variant="outlined"
                        >
                          <InputLabel id="gender-label">Gender</InputLabel>
                          <Select
                            labelId="gender-label"
                            id="gender"
                            name="gender"
                            value={values.gender}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            label="Gender"
                          >
                            <MenuItem value="MALE">Male</MenuItem>
                            <MenuItem value="FEMALE">Female</MenuItem>
                          </Select>
                          {touched.gender && errors.gender && (
                            <FormHelperText>{errors.gender}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          fullWidth
                          id="age"
                          name="age"
                          label="Age"
                          type="number"
                          value={values.age}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.age && Boolean(errors.age)}
                          helperText={touched.age && errors.age}
                          variant="outlined"
                          InputProps={{ inputProps: { min: 0 } }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          fullWidth
                          id="email"
                          name="email"
                          label="Email"
                          type="email"
                          value={values.email}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.email && Boolean(errors.email)}
                          helperText={touched.email && errors.email}
                          variant="outlined"
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          fullWidth
                          id="cell_phone"
                          name="cell_phone"
                          label="Cell Phone"
                          value={values.cell_phone}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={
                            touched.cell_phone && Boolean(errors.cell_phone)
                          }
                          helperText={touched.cell_phone && errors.cell_phone}
                          variant="outlined"
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          id="residential_address"
                          name="residential_address"
                          label="Residential Address"
                          value={values.residential_address}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={
                            touched.residential_address &&
                            Boolean(errors.residential_address)
                          }
                          helperText={
                            touched.residential_address &&
                            errors.residential_address
                          }
                          variant="outlined"
                          multiline
                          rows={3}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          id="telephone"
                          name="telephone"
                          label="Telephone (Optional)"
                          value={values.telephone}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          variant="outlined"
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Educational History Section - Only University */}
                <SectionTitle
                  icon={<FaGraduationCap />}
                  title="Educational History"
                />
                <Card
                  variant="outlined"
                  sx={{
                    mb: 4,
                    borderRadius: "10px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                    border: `1px solid ${colors.border}`,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: colors.primary,
                      py: 1,
                      px: 2,
                      borderTopLeftRadius: "10px",
                      borderTopRightRadius: "10px",
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ color: "white", fontWeight: 500 }}
                    >
                      Add your university education details
                    </Typography>
                  </Box>
                  <CardContent>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          name="educational_history[0].institute"
                          label="Institute"
                          value={values.educational_history[0]?.institute || ""}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={
                            touched.educational_history?.[0]?.institute &&
                            Boolean((errors.educational_history?.[0] as any)?.institute)
                          }
                          helperText={
                            touched.educational_history?.[0]?.institute &&
                            (errors.educational_history?.[0] as any)?.institute
                          }
                          variant="outlined"
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <FormControl component="fieldset">
                          <FormLabel
                            component="legend"
                            sx={{ color: colors.text }}
                          >
                            Scoring System
                          </FormLabel>
                          <RadioGroup
                            row
                            name="educational_history[0].score_is_percentage"
                            value={
                              values.educational_history[0]?.score_is_percentage
                                ? "percentage"
                                : "cgpa"
                            }
                            onChange={(e) =>
                              setFieldValue(
                                "educational_history[0].score_is_percentage",
                                e.target.value === "percentage"
                              )
                            }
                          >
                            <FormControlLabel
                              value="percentage"
                              control={<Radio />}
                              label="Percentage"
                            />
                            <FormControlLabel
                              value="cgpa"
                              control={<Radio />}
                              label="CGPA"
                            />
                          </RadioGroup>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          name="educational_history[0].score"
                          label={
                            values.educational_history[0]?.score_is_percentage
                              ? "Percentage (%)"
                              : "CGPA"
                          }
                          type="number"
                          value={values.educational_history[0]?.score || 0}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={
                            touched.educational_history?.[0]?.score &&
                            Boolean((errors.educational_history?.[0] as any)?.score)
                          }
                          helperText={
                            touched.educational_history?.[0]?.score &&
                            (errors.educational_history?.[0] as any)?.score
                          }
                          variant="outlined"
                          InputProps={{
                            inputProps: {
                              min: 0,
                              max: values.educational_history[0]
                                ?.score_is_percentage
                                ? 100
                                : 4,
                              step: values.educational_history[0]
                                ?.score_is_percentage
                                ? 1
                                : 0.1,
                            },
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          name="educational_history[0].current_semester"
                          label="Last Completed Semester"
                          type="number"
                          value={values.educational_history[0]?.current_semester}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={
                            touched.educational_history?.[0]?.current_semester &&
                            Boolean((errors.educational_history?.[0] as any)?.current_semester)
                          }
                          helperText={
                            touched.educational_history?.[0]?.current_semester &&
                            (errors.educational_history?.[0] as any)?.current_semester
                          }
                          variant="outlined"
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          name="educational_history[0].total_semesters"
                          label="Total Semesters"
                          type="number"
                          value={values.educational_history[0]?.total_semesters}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={
                            touched.educational_history?.[0]?.total_semesters &&
                            Boolean((errors.educational_history?.[0] as any)?.total_semesters)
                          }
                          helperText={
                            touched.educational_history?.[0]?.total_semesters &&
                            (errors.educational_history?.[0] as any)?.total_semesters
                          }
                          variant="outlined"
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Scholarship Request Details Section */}
                <SectionTitle
                  icon={<FaFileAlt />}
                  title="Scholarship Request Details"
                />
                <Card
                  variant="outlined"
                  sx={{
                    mb: 4,
                    borderRadius: "10px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                    border: `1px solid ${colors.border}`,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: colors.primary,
                      py: 1,
                      px: 2,
                      borderTopLeftRadius: "10px",
                      borderTopRightRadius: "10px",
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ color: "white", fontWeight: 500 }}
                    >
                      Provide scholarship request information
                    </Typography>
                  </Box>
                  <CardContent>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          name="scholarship_request_details.degree_name"
                          label="Degree Name"
                          value={values.scholarship_request_details.degree_name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={
                            touched.scholarship_request_details?.degree_name &&
                            Boolean(
                              errors.scholarship_request_details?.degree_name
                            )
                          }
                          helperText={
                            touched.scholarship_request_details?.degree_name &&
                            errors.scholarship_request_details?.degree_name
                          }
                          variant="outlined"
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          name="scholarship_request_details.degree_discipline"
                          label="Degree Discipline"
                          value={
                            values.scholarship_request_details.degree_discipline
                          }
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={
                            touched.scholarship_request_details
                              ?.degree_discipline &&
                            Boolean(
                              errors.scholarship_request_details
                                ?.degree_discipline
                            )
                          }
                          helperText={
                            touched.scholarship_request_details
                              ?.degree_discipline &&
                            errors.scholarship_request_details
                              ?.degree_discipline
                          }
                          variant="outlined"
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Autocomplete
                          fullWidth
                          freeSolo
                          options={[...INSTITUTES, "Other"]}
                          value={
                            values.scholarship_request_details
                              .degree_institute === "" ? null : 
                            values.scholarship_request_details
                              .degree_institute
                          }
                          onChange={(event, newValue) => {
                            if (newValue === "Other") {
                              setFieldValue(
                                "scholarship_request_details.degree_institute",
                                "OTHER"
                              );
                            } else {
                              setFieldValue(
                                "scholarship_request_details.degree_institute",
                                newValue || ""
                              );
                            }
                          }}
                          onInputChange={(event, newInputValue) => {
                            // Only update if not selecting "Other"
                            if (newInputValue !== "Other") {
                              setFieldValue(
                                "scholarship_request_details.degree_institute",
                                newInputValue
                              );
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              name="scholarship_request_details.degree_institute"
                              label="Degree Institute"
                              variant="outlined"
                              onBlur={handleBlur}
                              error={
                                touched.scholarship_request_details
                                  ?.degree_institute &&
                                Boolean(
                                  errors.scholarship_request_details
                                    ?.degree_institute
                                )
                              }
                              helperText={
                                touched.scholarship_request_details
                                  ?.degree_institute &&
                                errors.scholarship_request_details
                                  ?.degree_institute
                              }
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  "&:hover fieldset": {
                                    borderColor: colors.secondary,
                                  },
                                  "&.Mui-focused fieldset": {
                                    borderColor: colors.primary,
                                  },
                                },
                              }}
                            />
                          )}
                          sx={{
                            "& .MuiAutocomplete-inputRoot": {
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: colors.secondary,
                              },
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: colors.primary,
                              },
                            },
                          }}
                          noOptionsText="Type to add a custom institute"
                        />
                      </Grid>

                      {values.scholarship_request_details.degree_institute === "OTHER" && (
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            id="scholarship_request_details.degree_institute_custom"
                            name="scholarship_request_details.degree_institute_custom"
                            label="Enter Institute Name"
                            value={
                              values.scholarship_request_details
                                .degree_institute_custom || ""
                            }
                            onChange={(e) => {
                              setFieldValue(
                                "scholarship_request_details.degree_institute_custom",
                                e.target.value
                              );
                              // Also update the main field with the custom value
                              setFieldValue(
                                "scholarship_request_details.degree_institute",
                                e.target.value
                              );
                            }}
                            onBlur={handleBlur}
                            error={
                              touched.scholarship_request_details
                                ?.degree_institute &&
                              Boolean(
                                errors.scholarship_request_details
                                  ?.degree_institute
                              )
                            }
                            helperText={
                              touched.scholarship_request_details
                                ?.degree_institute &&
                              errors.scholarship_request_details
                                ?.degree_institute
                            }
                            variant="outlined"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "&:hover fieldset": {
                                  borderColor: colors.secondary,
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: colors.primary,
                                },
                              },
                            }}
                          />
                        </Grid>
                      )}


                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          name="scholarship_request_details.degree_recognized_by"
                          label="Recognized By"
                          value={
                            values.scholarship_request_details
                              .degree_recognized_by
                          }
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={
                            touched.scholarship_request_details
                              ?.degree_recognized_by &&
                            Boolean(
                              errors.scholarship_request_details
                                ?.degree_recognized_by
                            )
                          }
                          helperText={
                            touched.scholarship_request_details
                              ?.degree_recognized_by &&
                            errors.scholarship_request_details
                              ?.degree_recognized_by
                          }
                          variant="outlined"
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          name="scholarship_request_details.degree_semester"
                          label="Applying for Semester"
                          type="number"
                          value={
                            values.scholarship_request_details.degree_semester
                          }
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={
                            touched.scholarship_request_details
                              ?.degree_semester &&
                            Boolean(
                              errors.scholarship_request_details
                                ?.degree_semester
                            )
                          }
                          helperText={
                            touched.scholarship_request_details
                              ?.degree_semester &&
                            errors.scholarship_request_details?.degree_semester
                          }
                          variant="outlined"
                          InputProps={{ inputProps: { min: 1 } }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          name="scholarship_request_details.tuition_fee_per_semester"
                          label="Tuition Fee per Semester"
                          type="number"
                          value={
                            values.scholarship_request_details
                              .tuition_fee_per_semester
                          }
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={
                            touched.scholarship_request_details
                              ?.tuition_fee_per_semester &&
                            Boolean(
                              errors.scholarship_request_details
                                ?.tuition_fee_per_semester
                            )
                          }
                          helperText={
                            touched.scholarship_request_details
                              ?.tuition_fee_per_semester &&
                            errors.scholarship_request_details
                              ?.tuition_fee_per_semester
                          }
                          variant="outlined"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                PKR
                              </InputAdornment>
                            ),
                            inputProps: { min: 0 },
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          name="scholarship_request_details.tuition_fee_per_semester_in_words"
                          label="Tuition Fee per Semester (in words)"
                          value={
                            values.scholarship_request_details
                              .tuition_fee_per_semester_in_words
                          }
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={
                            touched.scholarship_request_details
                              ?.tuition_fee_per_semester_in_words &&
                            Boolean(
                              errors.scholarship_request_details
                                ?.tuition_fee_per_semester_in_words
                            )
                          }
                          helperText={
                            touched.scholarship_request_details
                              ?.tuition_fee_per_semester_in_words &&
                            errors.scholarship_request_details
                              ?.tuition_fee_per_semester_in_words
                          }
                          variant="outlined"
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          name="scholarship_request_details.admission_fee"
                          label="Admission Fee"
                          type="number"
                          value={
                            values.scholarship_request_details.admission_fee
                          }
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={
                            touched.scholarship_request_details
                              ?.admission_fee &&
                            Boolean(
                              errors.scholarship_request_details?.admission_fee
                            )
                          }
                          helperText={
                            touched.scholarship_request_details
                              ?.admission_fee &&
                            errors.scholarship_request_details?.admission_fee
                          }
                          variant="outlined"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                PKR
                              </InputAdornment>
                            ),
                            inputProps: { min: 0 },
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          name="scholarship_request_details.self_contribution_percentage"
                          label="How much tuition fee can you pay per semester? (%)"
                          type="number"
                          value={
                            values.scholarship_request_details
                              .self_contribution_percentage
                          }
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={
                            touched.scholarship_request_details
                              ?.self_contribution_percentage &&
                            Boolean(
                              errors.scholarship_request_details
                                ?.self_contribution_percentage
                            )
                          }
                          helperText={
                            touched.scholarship_request_details
                              ?.self_contribution_percentage &&
                            errors.scholarship_request_details
                              ?.self_contribution_percentage
                          }
                          variant="outlined"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                %
                              </InputAdornment>
                            ),
                            inputProps: { min: 0, max: 100 },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Guardian Details Section */}
                <SectionTitle icon={<FaUserTie />} title="Guardian Details" />
                <Card
                  variant="outlined"
                  sx={{
                    mb: 4,
                    borderRadius: "10px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                    border: `1px solid ${colors.border}`,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: colors.primary,
                      py: 1,
                      px: 2,
                      borderTopLeftRadius: "10px",
                      borderTopRightRadius: "10px",
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ color: "white", fontWeight: 500 }}
                    >
                      Provide father/guardian information
                    </Typography>
                  </Box>
                  <CardContent>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          name="guardian_details.name"
                          label="Father/Guardian Name"
                          value={values.guardian_details.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={
                            touched.guardian_details?.name &&
                            Boolean(errors.guardian_details?.name)
                          }
                          helperText={
                            touched.guardian_details?.name &&
                            errors.guardian_details?.name
                          }
                          variant="outlined"
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          name="guardian_details.cnic_number"
                          label="Father/Guardian CNIC Number"
                          value={values.guardian_details.cnic_number}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={
                            touched.guardian_details?.cnic_number &&
                            Boolean(errors.guardian_details?.cnic_number)
                          }
                          helperText={
                            touched.guardian_details?.cnic_number &&
                            errors.guardian_details?.cnic_number
                          }
                          variant="outlined"
                          placeholder="12345-1234567-1"
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          name="guardian_details.cell_phone"
                          label="Father/Guardian Cell Phone"
                          value={values.guardian_details.cell_phone}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={
                            touched.guardian_details?.cell_phone &&
                            Boolean(errors.guardian_details?.cell_phone)
                          }
                          helperText={
                            touched.guardian_details?.cell_phone &&
                            errors.guardian_details?.cell_phone
                          }
                          variant="outlined"
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          name="guardian_details.occupation"
                          label="Father/Guardian Occupation"
                          value={values.guardian_details.occupation}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={
                            touched.guardian_details?.occupation &&
                            Boolean(errors.guardian_details?.occupation)
                          }
                          helperText={
                            touched.guardian_details?.occupation &&
                            errors.guardian_details?.occupation
                          }
                          variant="outlined"
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          name="guardian_details.designation"
                          label="Father/Guardian Designation"
                          value={values.guardian_details.designation}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={
                            touched.guardian_details?.designation &&
                            Boolean(errors.guardian_details?.designation)
                          }
                          helperText={
                            touched.guardian_details?.designation &&
                            errors.guardian_details?.designation
                          }
                          variant="outlined"
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          name="guardian_details.employer"
                          label="Father/Guardian Employer"
                          value={values.guardian_details.employer}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={
                            touched.guardian_details?.employer &&
                            Boolean(errors.guardian_details?.employer)
                          }
                          helperText={
                            touched.guardian_details?.employer &&
                            errors.guardian_details?.employer
                          }
                          variant="outlined"
                        />
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          name="guardian_details.monthly_income"
                          label="Monthly Income"
                          type="number"
                          value={values.guardian_details.monthly_income}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={
                            touched.guardian_details?.monthly_income &&
                            Boolean(errors.guardian_details?.monthly_income)
                          }
                          helperText={
                            touched.guardian_details?.monthly_income &&
                            errors.guardian_details?.monthly_income
                          }
                          variant="outlined"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                PKR
                              </InputAdornment>
                            ),
                            inputProps: { min: 0 },
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          name="guardian_details.pension"
                          label="Pension (if any)"
                          type="number"
                          value={values.guardian_details.pension}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          variant="outlined"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                PKR
                              </InputAdornment>
                            ),
                            inputProps: { min: 0 },
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          name="guardian_details.family_members_count"
                          label="Family Members Count"
                          type="number"
                          value={values.guardian_details.family_members_count}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={
                            touched.guardian_details?.family_members_count &&
                            Boolean(
                              errors.guardian_details?.family_members_count
                            )
                          }
                          helperText={
                            touched.guardian_details?.family_members_count &&
                            errors.guardian_details?.family_members_count
                          }
                          variant="outlined"
                          InputProps={{ inputProps: { min: 0 } }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Documents Section */}
                <SectionTitle icon={<FaFileAlt />} title="Documents" />
                <Card
                  variant="outlined"
                  sx={{
                    mb: 4,
                    borderRadius: "10px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                    border: `1px solid ${colors.border}`,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: colors.primary,
                      py: 1,
                      px: 2,
                      borderTopLeftRadius: "10px",
                      borderTopRightRadius: "10px",
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ color: "white", fontWeight: 500 }}
                    >
                      Upload required documents
                    </Typography>
                  </Box>
                  <CardContent>
                    <FieldArray name="documents_attached">
                      {() => (
                        <Box>
                          {values.documents_attached.map((document, index) => (
                            <Box
                              key={index}
                              sx={{
                                mb: 3,
                                p: 2,
                                border: `1px solid ${colors.border}`,
                                borderRadius: "8px",
                                bgcolor: "white",
                                transition: "all 0.3s ease",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  mb: 2,
                                }}
                              >
                                <Typography
                                  variant="subtitle1"
                                  sx={{
                                    color: colors.primary,
                                    fontWeight: 600,
                                  }}
                                >
                                  Document #{index + 1}
                                </Typography>
                              </Box>

                              <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                  <TextField
                                    fullWidth
                                    name={`documents_attached.${index}.title`}
                                    label="Document Title"
                                    value={document.title}
                                    variant="outlined"
                                    InputProps={{ readOnly: true }}
                                  />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                  <FormControl
                                    fullWidth
                                    error={
                                      touched.documents_attached?.[index]
                                        ?.attachment_url &&
                                      Boolean(
                                        (
                                          errors.documents_attached?.[
                                            index
                                          ] as any
                                        )?.attachment_url
                                      )
                                    }
                                  >
                                    <InputLabel
                                      shrink
                                      htmlFor={`document-upload-${index}`}
                                    >
                                      Document File
                                    </InputLabel>
                                    <Box sx={{ mt: 2 }}>
                                      <input
                                        accept="application/pdf,image/*"
                                        id={`document-upload-${index}`}
                                        type="file"
                                        style={{ display: "none" }}
                                        onChange={(e) =>
                                          handleDocumentUpload(
                                            e,
                                            index,
                                            setFieldValue,
                                            values
                                          )
                                        }
                                      />
                                      <label
                                        htmlFor={`document-upload-${index}`}
                                      >
                                        <Button
                                          variant="outlined"
                                          component="span"
                                          disabled={uploadingDocuments[index]}
                                          startIcon={<FaUpload />}
                                          sx={{
                                            borderColor: colors.secondary,
                                            color: colors.secondary,
                                            "&:hover": {
                                              borderColor: colors.primary,
                                              backgroundColor:
                                                "rgba(30, 77, 140, 0.04)",
                                            },
                                          }}
                                        >
                                          {uploadingDocuments[index]
                                            ? "Uploading..."
                                            : "Upload Document"}
                                        </Button>
                                      </label>

                                      {document.attachment_url.length > 0 && (
                                        <Box sx={{ mt: 1 }}>
                                          {document.attachment_url.map(
                                            (u, i) => (
                                              <Box
                                                key={i}
                                                sx={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  mt: 0.5,
                                                }}
                                              >
                                                <MuiLink
                                                  href={u}
                                                  target="_blank"
                                                  rel="noopener"
                                                  variant="body2"
                                                  underline="hover"
                                                  sx={{
                                                    color: colors.primary,
                                                    mr: 1,
                                                    flex: 1,
                                                    minWidth: 0, // Allow shrinking
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                  }}
                                                >
                                                  {u.split("/").pop()}
                                                </MuiLink>
                                                <IconButton
                                                  size="small"
                                                  onClick={() => {
                                                    const newUrls =
                                                      document.attachment_url.filter(
                                                        (_, idx) => idx !== i
                                                      );
                                                    setFieldValue(
                                                      `documents_attached.${index}.attachment_url`,
                                                      newUrls
                                                    );
                                                  }}
                                                  sx={{ 
                                                    color: colors.error,
                                                    flexShrink: 0, // Don't shrink the delete button
                                                    minWidth: "auto",
                                                  }}
                                                >
                                                  <FaTrash />
                                                </IconButton>
                                              </Box>
                                            )
                                          )}
                                        </Box>
                                      )}
                                      {touched.documents_attached?.[index]
                                        ?.attachment_url &&
                                        (
                                          errors.documents_attached?.[
                                            index
                                          ] as any
                                        )?.attachment_url && (
                                          <FormHelperText error>
                                            {
                                              (
                                                errors.documents_attached?.[
                                                  index
                                                ] as any
                                              )?.attachment_url
                                            }
                                          </FormHelperText>
                                        )}
                                    </Box>
                                  </FormControl>
                                </Grid>
                              </Grid>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </FieldArray>
                  </CardContent>
                </Card>

                {/* Submit Button */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    mt: 4,
                    mb: 2,
                  }}
                >
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={loading}
                    startIcon={<FaSave />}
                    sx={{
                      py: 1.5,
                      px: 6,
                      bgcolor: colors.primary,
                      "&:hover": {
                        bgcolor: colors.secondary,
                      },
                      fontSize: "1.1rem",
                      fontWeight: 600,
                      color: "white",
                      boxShadow: "0 4px 10px rgba(30, 77, 140, 0.2)",
                      transition: "all 0.3s ease",
                    }}
                  >
                    {loading ? "Submitting..." : "Submit Reapplication"}
                  </Button>
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      </Paper>
    </Container>
  );
};
